import type Highcharts from 'highcharts';
import { isDate } from 'lodash';

import type { CategoryType } from '../types';

/**
 * Normalise a subcategory key to the stable identity threaded onto each series
 * as `subCategoryKey` — dates are stringified so they compare equal across the
 * data-series boundary (RSP-6045).
 */
const normaliseSubCategoryKey = (key: CategoryType): number | string =>
  isDate(key) ? String(key) : key;

/**
 * Resolve a clicked stack segment back to its subcategory so the clickthrough
 * can filter by both category and subcategory (RSP-6045).
 *
 * Matches on the stable `subCategoryKey` threaded onto each series (see
 * `useDataSeries`) rather than the display title, which a
 * `subCategoryOverrideFunction` can rename. Returns `undefined` for non-stacked
 * clicks, where the series carries no `subCategoryKey`.
 */
export const resolveClickedSubCategory = <
  SubCategory extends { key: CategoryType },
>(
  category: { subCategories?: SubCategory[] },
  event: Highcharts.SeriesClickEventObject
): SubCategory | undefined => {
  const subCategoryKey = event.point.series.userOptions.custom?.subCategoryKey;

  return category.subCategories?.find(
    (sc) => normaliseSubCategoryKey(sc.key) === subCategoryKey
  );
};
